import type { Vec2 } from '../../utils/vec2.js';
import type { EntityId, PlayerId, Tick } from '../ids.js';

import type { ProductionSlot } from './Core.js';
import type { SquadTargetRef } from './Squad.js';

// Permanent building kinds (player-built via BuildCommand). 'mini_turret' is a
// transient ability-spawned building; it lives in the same buildings table and
// reuses CombatSystem's turret loop, but is not buildable from the BuildPanel.
export type PermanentBuildingKind = 'production_hub' | 'turret';
export type BuildingKind = PermanentBuildingKind | 'mini_turret';

// Buildings are minted on a successful BuildCommand at the player's Core slot. The
// `coreId` + `slotIndex` pair is the canonical occupancy key — BuildingSystem reads
// it to know which slot is filled. `buildProgress` advances 0→1 in BuildingSystem;
// the building is "operational" once it reaches 1. Operational hubs accept
// ProduceSquadCommand; operational turrets fire via CombatSystem.
//
// Splitting into a discriminated union keeps the Hub/Turret runtime fields apart so
// systems can rely on the kind narrowing rather than null-checks on optional fields.
// V1.5+ forward turret network dependency: every Building carries `isOnline`.
// For `mini_turret` it is derived per tick by ForwardTurretNetworkSystem from
// the turret's proximity to any owned, connected ControlNode (see
// data/forwardTurret.ts). For permanent buildings (`production_hub`,
// `turret`) it stays `true` — their Core-ring placement makes them inherently
// network-rooted (docs/02_GAME_DESIGN.md §15 "Core-adjacent defensive turrets
// always work"). Default `true` on construction so the build-progress phase
// doesn't render dimmed before the system ticks for the first time.
export interface ProductionHub {
  id: EntityId;
  ownerId: PlayerId;
  kind: 'production_hub';
  pos: Vec2;
  hp: number;
  maxHp: number;
  buildProgress: number;
  coreId: EntityId;
  slotIndex: number;
  produces: ProductionSlot;
  isOnline: boolean;
}

export interface Turret {
  id: EntityId;
  ownerId: PlayerId;
  kind: 'turret';
  pos: Vec2;
  hp: number;
  maxHp: number;
  buildProgress: number;
  coreId: EntityId;
  slotIndex: number;
  attackCooldown: number;
  target: SquadTargetRef | null;
  facing: number;
  isOnline: boolean;
}

// MiniTurret is the V1 Engineer ability spawn. It is anti-infantry, low-HP, and
// expires when hp ≤ 0 OR state.tick reaches expiresAtTick. It does not occupy a
// build slot — coreId/slotIndex are absent — and its ownerId is the player whose
// Engineer cast the ability. CombatSystem treats it like a turret (anti-infantry
// only); BuildingSystem reaps it by hp OR by tick, whichever comes first.
//
// V1.5+ network dependency: `isOnline` is reconciled by
// ForwardTurretNetworkSystem each tick. Offline mini-turrets do NOT fire and
// do NOT contribute vision — the dramatic battlefield consequence
// docs/02_GAME_DESIGN.md §15 specifies.
export interface MiniTurret {
  id: EntityId;
  ownerId: PlayerId;
  kind: 'mini_turret';
  pos: Vec2;
  hp: number;
  maxHp: number;
  buildProgress: number;
  attackCooldown: number;
  target: SquadTargetRef | null;
  facing: number;
  expiresAtTick: Tick;
  isOnline: boolean;
}

export type Building = ProductionHub | Turret | MiniTurret;

export function isOperational(b: Building): boolean {
  return b.buildProgress >= 1;
}
